import React, { useEffect, useState, useRef } from 'react';
import { ownerFirstName } from '../lib/ownerName';
import { User } from '@supabase/supabase-js';
import { supabase, logout } from '../lib/supabase';
import { getUserCurrency } from '../lib/currency';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus, Trash2, CheckCircle2, Clock, ChevronRight,
  MessageSquare, Bell, Eye, Lock, Crown, Zap, Sparkles,
  Sun, Moon, LayoutGrid, PlusCircle, LogOut, UserCircle, Inbox, Gem, Users,
} from 'lucide-react';
import { LoyaltyTest, UserProfile } from '../types';
import { useTheme } from '../lib/ThemeContext';
import { enablePushForCreator, relinkPushIfGranted, isPushCapable, hasPushPermission, isIosNotInstalled } from '../lib/push';
import { PlatformBadge } from '../components/PlatformBadge';
import { PlatformId } from '../components/PlatformIcons';
import SessionSentPrompt from '../components/SessionSentPrompt';
import PremiumModal from '../components/PremiumModal';
import { track } from '../lib/analytics';
import OpenedUnlockModal from '../components/OpenedUnlockModal';
import { getReferralStatus, ReferralStatus } from '../lib/referral';
import { getGuessCounts } from '../lib/guesses';
import { isFollowUpDispatch } from '../lib/dispatchMessages';

interface DashboardProps { user: User; }
declare global { interface Window { PaystackPop: any; } }

const FREE_REMINDER_LIMIT = 1;
// Prices are the same number in every currency (20 = ₵20 = ₦20 = $20 etc.)
const PREMIUM_PRICE = 2000;      // 2000 subunits = 20 in any currency
const EXTRA_REMINDERS_PRICE = 100; // 100 subunits = 1 in any currency


const INJECTED_CSS = `
.yhuu-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}
@media (min-width: 600px) {
  .yhuu-grid { grid-template-columns: repeat(2,1fr); gap:16px; }
}
@media (min-width: 960px) {
  .yhuu-grid { grid-template-columns: repeat(3,1fr); gap:18px; }
}
.yhuu-stats {
  display: grid;
  grid-template-columns: repeat(3,1fr);
  gap: 10px;
  margin-bottom: 28px;
}
.yhuu-hero-row {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 28px;
}
@media (min-width: 640px) {
  .yhuu-hero-row {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
}
.yhuu-hero-actions {
  display: flex;
  gap: 10px;
  align-items: stretch;
  flex-wrap: nowrap;
}
@keyframes yhuu-cta-glow {
  0%, 100% { box-shadow: 0 10px 26px rgba(225,29,72,0.38); transform: scale(1); }
  50% { box-shadow: 0 14px 38px rgba(236,72,153,0.62); transform: scale(1.025); }
}
.yhuu-new-btn { animation: yhuu-cta-glow 2.6s ease-in-out infinite; transition: filter .15s; }
.yhuu-new-btn:active { transform: scale(0.97); animation: none; }
@media (prefers-reduced-motion: reduce) { .yhuu-new-btn { animation: none; } }
.yhuu-stat-num {
  font-size: clamp(26px, 6vw, 36px);
  font-weight: 900;
  letter-spacing: -1px;
  color: var(--d-text);
}
.yhuu-stat-num.red { color: #dc2626; }
.yhuu-stat-label {
  font-size: clamp(9px, 2.2vw, 11px);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--d-muted);
  margin-top: 4px;
}
.yhuu-card-name {
  font-size: clamp(16px, 4vw, 21px);
  font-weight: 800;
  color: var(--d-text);
  letter-spacing: -0.5px;
  margin-bottom: 3px;
  line-height: 1.2;
}
`;

export default function Dashboard({ user }: DashboardProps) {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // Same link construction as Profile.tsx — the yhuu_uid/yhuu_name params let
  // the Yhuu Test app pre-fill the creator's identity and gate their one free unlock.
  const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
  const ownerName = ownerFirstName(user.email);
  const testLink = `/test?yhuu_uid=${encodeURIComponent(user.id)}&yhuu_name=${encodeURIComponent(displayName)}`;
  const [tests, setTests] = useState<LoyaltyTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [reminderCounts, setReminderCounts] = useState<Record<string, number>>({});
  const [toast, setToast] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState('₵');
  const [currencyCode, setCurrencyCode] = useState('GHS');
  const [inboxCount, setInboxCount] = useState(0);
  const [pushGranted, setPushGranted] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  // null until the referral SQL has been run — the whole friends-unlock UI stays hidden then.
  const [referral, setReferral] = useState<ReferralStatus | null>(null);
  const [premiumBadgeSeen, setPremiumBadgeSeen] = useState(() => {
    try { return localStorage.getItem('yhuu_premium_badge_seen') === '1'; } catch { return false; }
  });

  // A session was just sent (either straight from CreateTest, or via the
  // auto-submit-after-signup flow) — both land here with ?newTest=<id>.
  // Offer the Yhuu Test cross-promo once, right on arrival.
  const justSentTestId = new URLSearchParams(location.search).get('newTest');
  const [showSentPrompt, setShowSentPrompt] = useState(!!justSentTestId);

  const dismissSentPrompt = () => {
    setShowSentPrompt(false);
    navigate('/dashboard', { replace: true });
  };

  useEffect(() => {
    getUserCurrency().then(c => {
      setCurrencySymbol(c.symbol);
      setCurrencyCode(c.code);
    });
  }, []);

  useEffect(() => {
    if (!document.getElementById('yhuu-css')) {
      const el = document.createElement('style');
      el.id = 'yhuu-css';
      el.textContent = INJECTED_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    const existing = document.getElementById('paystack-script') as HTMLScriptElement | null;
    if (existing) {
      // Script tag already exists (e.g. from another page/component in this
      // session) — if it already finished loading, PaystackPop will be on
      // window; otherwise wait for its load event too.
      if (window.PaystackPop) {
        setPaystackLoaded(true);
      } else {
        existing.addEventListener('load', () => setPaystackLoaded(true));
      }
      return;
    }
    const s = document.createElement('script');
    s.id = 'paystack-script';
    s.src = 'https://js.paystack.co/v1/inline.js';
    s.onload = () => setPaystackLoaded(true);
    document.body.appendChild(s);
  }, []);

  useEffect(() => { fetchAll(); fetchInboxCount(); }, [user.id]);
  useEffect(() => { getReferralStatus().then(setReferral); }, [user.id]);
  // How many guesses each session has (0 / empty until supabase/guesses.sql has been run).
  const [guessCounts, setGuessCounts] = useState<Record<string, number>>({});
  useEffect(() => { getGuessCounts().then(setGuessCounts); }, [user.id]);

  // Push notifications: quietly re-subscribe if permission was already
  // granted before (never prompts). iOS "not installed yet" is handled by
  // the banner below.
  useEffect(() => {
    setPushGranted(hasPushPermission());
    relinkPushIfGranted(user.id).then(() => setPushGranted(hasPushPermission()));
  }, [user.id]);

  const handleEnablePush = async () => {
    if (pushLoading) return;
    setPushLoading(true);
    try {
      const result = await enablePushForCreator(user.id);
      if (result === 'granted') {
        setPushGranted(true);
        track('push_enabled');
        showToast('Push notifications enabled 🔔');
      } else if (result === 'denied') {
        showToast('Notification permission was blocked — enable it in your browser settings.');
      } else if (result.startsWith('error:')) {
        showToast(`Push setup failed: ${result.slice(6)}`);
      } else {
        showToast("Push isn't supported on this device/browser yet.");
      }
    } finally {
      setPushLoading(false);
    }
  };

  const fetchInboxCount = async () => {
    const { data: tests } = await supabase.from('tests').select('id').eq('creator_id', user.id);
    if (!tests?.length) return;
    const { count } = await supabase
      .from('retake_notifications')
      .select('id', { count: 'exact', head: true })
      .in('test_id', tests.map(t => t.id))
      .eq('status', 'pending');
    setInboxCount(count ?? 0);
  };

  const fetchAll = async () => { await Promise.all([fetchTests(), fetchProfile()]); };

  const fetchTests = async () => {
    try {
      const { data, error } = await supabase
        .from('tests').select('*')
        .eq('creator_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data || []) as LoyaltyTest[];
      setTests(rows);
      await fetchReminderCounts(rows.map(t => t.id!).filter(Boolean));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchProfile = async () => {
    const { data } = await supabase.from('profiles').select('id, premium_until').eq('id', user.id).maybeSingle();
    if (data) {
      const isPrem = data.premium_until ? new Date(data.premium_until) > new Date() : false;
      setProfile({ id: data.id, is_premium: isPrem, premium_until: data.premium_until ?? null });
    } else {
      setProfile({ id: user.id, is_premium: false, premium_until: null });
    }
  };

  const fetchReminderCounts = async (testIds: string[]) => {
    if (!testIds.length) return;
    const { data } = await supabase.from('reminders').select('test_id').in('test_id', testIds);
    const counts: Record<string, number> = {};
    (data || []).forEach((r: { test_id: string }) => { counts[r.test_id] = (counts[r.test_id] || 0) + 1; });
    setReminderCounts(counts);
  };

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  const isPremium = profile?.is_premium ?? false;
  const reminderLimit = (t: LoyaltyTest) => FREE_REMINDER_LIMIT + (t.bonus_reminders ?? 0);
  const remindersUsed = (t: LoyaltyTest) => reminderCounts[t.id!] ?? 0;
  const isOpenedUnlocked = (t: LoyaltyTest) => {
    if (isPremium) return true;
    if (t.opened_unlocked) return true; // sent during a friends-unlock window
    if (!t.opened_notif_paid_until) return false;
    return new Date(t.opened_notif_paid_until) > new Date();
  };
  // Push notifications are a paid perk: only show the "enable push" banner to
  // creators who are Premium, or who've unlocked "see if opened" on at least
  // one session — otherwise there's nothing for a push to notify them about.


  const deleteTest = async (id: string) => {
    if (confirm('Are you sure you want to delete this session?')) {
      const { error } = await supabase.from('tests').delete().eq('id', id);
      if (!error) setTests(tests.filter(t => t.id !== id));
    }
  };

  function isUSNumber(phone: string) { const c = phone.replace(/\s+/g, ''); return c.startsWith('+1') || (c.startsWith('1') && c.length === 11); }
  function shouldSendInstantly(platform: string) { return platform === 'WhatsApp'; }

  const requestReminder = async (testId: string) => {
    const test = tests.find(t => t.id === testId);
    if (!test) return;
    const instant = shouldSendInstantly(test.partner_platform);
    const { error } = await supabase.from('reminders').insert([{ test_id: testId, requested_by: user.id, status: instant ? 'sent' : 'pending', created_at: new Date().toISOString() }]);
    if (error) { showToast('Failed to send reminder.'); return; }
    if (instant) {
      await supabase.functions.invoke('send-message', {
        body: {
          to: test.partner_contact,
          name: test.partner_name,
          link: `https://yhuu.life/test/${test.id}`,
          channel: test.partner_platform,
          isReminder: true,
        },
      });
    }
    setReminderCounts(prev => ({ ...prev, [testId]: (prev[testId] ?? 0) + 1 }));
    showToast(instant ? 'Reminder sent!' : 'Reminder queued for dispatch.');
  };

  // Central helper: verifies a Paystack reference server-side and returns the parsed result.
  // The edge function uses the secret key — no client-side trust needed.
  const verifyWithServer = async (reference: string): Promise<{ success: boolean; [key: string]: any }> => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ reference }),
    });
    if (!res.ok) return { success: false };
    return res.json();
  };

  const paystackFlow = (amount: number, metadata: Record<string, any>, onSuccess: (result: any) => Promise<void>, processingKey: string) => {
    if (processingId) return;
    if (!paystackLoaded || !window.PaystackPop) {
      showToast('Payment system is still loading — please try again in a moment.');
      return;
    }
    setProcessingId(processingKey);
    // Everything from here on can throw synchronously (bad key, Paystack's
    // internal state not being ready, etc). Without a try/catch, a throw here
    // leaves processingId set forever and the button stuck on "Processing...".
    try {
      const handler = window.PaystackPop.setup({
        key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
        email: user.email, amount, currency: currencyCode, metadata,
        // NOTE: Paystack's inline.js only ever calls "callback" (on success) and
        // "onClose" (when the popup closes, whether cancelled or after success).
        // Names like onSuccess/onCancel/onFailure are never invoked by the library.
        // IMPORTANT: this must be a plain (non-async) function. Paystack's
        // v1/inline.js checks the callback's type against `[object Function]`
        // — an `async` function is internally tagged `[object AsyncFunction]`
        // and fails that check, throwing "Attribute callback must be a valid
        // function" even though it's a real, callable function. The await
        // logic lives in an inner async IIFE instead.
        callback: (transaction: { reference: string }) => {
          (async () => {
            try {
              const result = await verifyWithServer(transaction.reference);
              if (result.success) {
                await onSuccess(result);
              } else {
                showToast('Payment received but activation failed. Contact support with your reference: ' + transaction.reference);
              }
            } catch {
              showToast('Could not confirm payment. Contact support with your reference: ' + transaction.reference);
            } finally {
              setProcessingId(null);
            }
          })();
        },
        onClose: () => setProcessingId(null),
      });
      handler.openIframe();
    } catch (err) {
      console.error('Paystack setup failed:', err);
      showToast('Payment system could not start. Please refresh and try again.');
      setProcessingId(null);
    }
  };

  const upgradeToPremium = () => paystackFlow(
    PREMIUM_PRICE,
    { user_id: user.id, feature: 'premium' },
    async (result) => {
      setProfile({ id: user.id, is_premium: true, premium_until: result.premium_until });
      showToast('Welcome to Premium! 🎉');
    },
    'premium'
  );

  const buyExtraReminders = (test: LoyaltyTest) => paystackFlow(
    EXTRA_REMINDERS_PRICE,
    { test_id: test.id, feature: 'bonus_reminders' },
    async (result) => {
      setTests(prev => prev.map(t => t.id === test.id ? { ...t, bonus_reminders: result.bonus_reminders } : t));
      showToast('+2 reminders added!');
    },
    test.id!
  );

  const unlockOpenedStatus = (test: LoyaltyTest) => paystackFlow(
    PREMIUM_PRICE,
    { test_id: test.id, feature: 'opened_notif' },
    async (result) => {
      setTests(prev => prev.map(t => t.id === test.id ? { ...t, opened_notif_paid_until: result.paid_until } : t));
      showToast('Unlocked! Opened status visible for 30 days.');
    },
    test.id!
  );

  const totalSent = tests.length;
  const totalCompleted = tests.filter(t => t.status === 'completed').length;
  const totalWaiting = tests.filter(t => t.status !== 'completed').length;

  // Fires the bundled "limit_reached" push exactly once per test/reason —
  // guarded with localStorage so it survives remounts/re-renders and never
  // repeats across sessions either.
  const notifiedLimitsRef = useRef<Set<string>>(new Set());
  const notifyLimitReached = (reason: 'reminder' | 'rating' | 'retake', testId: string) => {
    if (isPremium) return; // safety net — server also checks this
    const key = `yhuu_limit_notif_${reason}_${testId}`;
    if (notifiedLimitsRef.current.has(key) || localStorage.getItem(key)) return;
    notifiedLimitsRef.current.add(key);
    localStorage.setItem(key, '1');
    supabase.functions
      .invoke('send-push', { body: { type: 'limit_reached', profileId: user.id, reason, testId } })
      .catch(() => {});
  };

  // ── Sub-components ──────────────────────────────────────────────────────────
  const ReminderSection = ({ test }: { test: LoyaltyTest }) => {
    const used = remindersUsed(test);
    const limit = reminderLimit(test);
    const exhausted = !isPremium && used >= limit;

    useEffect(() => {
      if (exhausted) notifyLimitReached('reminder', test.id!);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exhausted, test.id]);

    if (!exhausted) return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button onClick={() => requestReminder(test.id!)} style={btn('ghost')}>
          <Bell size={13} /> Remind · {used} sent
        </button>
        {!isPremium && used > 0 && (
          <button onClick={() => buyExtraReminders(test)} disabled={processingId === test.id} style={btn('ghost', processingId === test.id)}>
            <Zap size={13} />{processingId === test.id ? 'Processing...' : `+2 Reminders · ${currencySymbol}1`}
          </button>
        )}
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button onClick={upgradeToPremium} disabled={processingId === 'premium'} style={btn('gold', processingId === 'premium')}>
          <Crown size={13} />{processingId === 'premium' ? 'Processing...' : `Premium · ${currencySymbol}20/mo`}
        </button>
        <button onClick={() => buyExtraReminders(test)} disabled={processingId === test.id} style={btn('ghost', processingId === test.id)}>
          <Zap size={13} />{processingId === test.id ? 'Processing...' : `+2 Reminders · ${currencySymbol}1`}
        </button>
      </div>
    );
  };

  const OpenedStatusSection = ({ test }: { test: LoyaltyTest }) => {
    if (isOpenedUnlocked(test)) {
      return (test as any).opened_at
        ? <div style={badge('yellow')}><Eye size={13} /> Opened · Not Answered</div>
        : <div style={badge('muted')}><Clock size={13} /> Not Opened Yet</div>;
    }
    return (
      <button className="yhuu-open-gold-btn" data-track="dash_have_they_opened" onClick={() => (referral ? setShowUnlockModal(true) : setShowPremiumModal(true))} disabled={processingId === test.id} style={{ ...btn('gold', processingId === test.id), gap: 8 }}>
        <Lock size={13} />
        Have They Opened It? 😬
        <Gem size={14} fill="#f59e0b" color="#f59e0b" />
      </button>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: 'var(--d-bg)', minHeight: '100vh', paddingBottom: 60 }}>
      {showSentPrompt && <SessionSentPrompt user={user} onDismiss={dismissSentPrompt} />}
      {showUnlockModal && referral && (
        <OpenedUnlockModal
          referral={referral}
          onClose={() => { setShowUnlockModal(false); getReferralStatus().then(setReferral); }}
          onUpgrade={() => { setShowUnlockModal(false); upgradeToPremium(); }}
          premiumProcessing={processingId === 'premium'}
          currencySymbol={currencySymbol}
        />
      )}
      {showPremiumModal && (
        <PremiumModal
          onClose={() => setShowPremiumModal(false)}
          onUpgrade={() => { setShowPremiumModal(false); upgradeToPremium(); }}
          isPremium={isPremium}
          processing={processingId === 'premium'}
          currencySymbol={currencySymbol}
        />
      )}

      {/* Nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 17, color: '#fff' }}>Y</div>
          <span style={{ fontSize: 15, fontWeight: 900, color: 'var(--d-text)', letterSpacing: 4, textTransform: 'uppercase' }}>Yhuu</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => {
              setShowPremiumModal(true);
              setPremiumBadgeSeen(true);
              try { localStorage.setItem('yhuu_premium_badge_seen', '1'); } catch {}
            }}
            style={{ ...iconBtn(), color: '#f59e0b', position: 'relative' }}
            aria-label="Yhuu Premium"
          >
            <Gem size={16} fill="#f59e0b" />
            {!isPremium && !premiumBadgeSeen && (
              <span style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--d-bg)' }}>
                1
              </span>
            )}
          </button>
          <button onClick={toggleTheme} style={iconBtn()}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link to="/inbox" style={{ ...iconBtn(), textDecoration: 'none', color: 'var(--d-muted)', position: 'relative' }}>
            <Inbox size={18} />
            {inboxCount > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {inboxCount > 9 ? '9+' : inboxCount}
              </span>
            )}
          </Link>
          <Link to="/profile" style={{ ...iconBtn(), textDecoration: 'none', color: 'var(--d-muted)' }}>
            <UserCircle size={18} />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '28px 20px 0' }}>

        {/* Hero */}
        <div className="yhuu-hero-row">
          <div>
            <div style={{ fontSize: 'clamp(28px, 8vw, 42px)', fontWeight: 900, color: 'var(--d-text)', letterSpacing: -1.5, lineHeight: 1.05, marginBottom: 8 }}>
              {ownerName ? (
                <><span style={{ color: '#dc2626' }}>{ownerName}{/s$/i.test(ownerName) ? "'" : "'s"}</span> Yhuu</>
              ) : 'Yhuu'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--d-muted)', fontWeight: 500, lineHeight: 1.5 }}>
              Track your active sessions and review the answers you've received.
            </div>
          </div>

          {/* Action buttons — Premium now lives behind the small gem icon
              in the nav, so this row is just the primary action. */}
          <div className="yhuu-hero-actions">
            <Link to="/create" className="yhuu-new-btn" data-track="dash_new_session"
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '20px 28px', borderRadius: 999, background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff', fontSize: 18, fontWeight: 900, textDecoration: 'none', whiteSpace: 'nowrap', minWidth: 240 }}>
              <Plus size={22} strokeWidth={3} /> New Session
            </Link>
          </div>
        </div>

        {/* Yhuu Test — separate compatibility quiz app, given its own banner
            here so it isn't buried in Profile. Full-width so it doesn't
            crowd the New Session / Premium buttons above on mobile. */}
        <a href={testLink} data-track="dash_yhuu_test"
          style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 18px', borderRadius: 22, background: 'linear-gradient(90deg, #7c3aed, #db2777)', textDecoration: 'none', marginBottom: 20, boxShadow: '0 12px 30px rgba(124,58,237,0.35)' }}>
          <span style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Sparkles size={22} color="#fff" />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: '#fff', letterSpacing: -0.3 }}>Take the Yhuu Test</div>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.92)', fontWeight: 600, marginTop: 2, lineHeight: 1.35 }}>See how compatible you are with someone — takes 2 minutes</div>
          </div>
          <ChevronRight size={22} color="#fff" style={{ flexShrink: 0 }} />
        </a>

        {/* Friends-unlock: bring 3 friends → see who opened your sessions for 3 days */}
        {referral && !isPremium && (() => {
          const active = !!referral.window_until;
          const earned = !active && referral.credits > 0;
          const days = active ? Math.max(1, Math.ceil((new Date(referral.window_until!).getTime() - Date.now()) / 86400000)) : 0;
          return (
            <button
              onClick={() => setShowUnlockModal(true)} data-track="dash_unlock_card"
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 18, marginBottom: 20, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                background: active ? '#ecfdf5' : 'var(--d-stat-bg)', border: `1.5px solid ${active ? '#6ee7b7' : 'var(--d-border)'}` }}
            >
              <span style={{ width: 38, height: 38, borderRadius: 12, background: active ? '#10b981' : 'linear-gradient(135deg,#e11d48,#ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {active ? <Eye size={18} color="#fff" /> : <Users size={18} color="#fff" />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 900, color: active ? '#047857' : 'var(--d-text)' }}>
                  {active ? `Opened status unlocked · ${days} day${days === 1 ? '' : 's'} left` : earned ? 'You earned it! Send a session to start your 3 days' : 'See who opened your sessions — free'}
                </span>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginTop: 2, color: active ? '#059669' : 'var(--d-muted)' }}>
                  {active ? 'Every session you send now shows if it was opened.' : earned ? 'Tap for details.' : `Share Yhuu with 3 friends · ${referral.progress} of 3 counted`}
                </span>
              </span>
              <ChevronRight size={18} style={{ flexShrink: 0, color: active ? '#059669' : 'var(--d-muted)' }} />
            </button>
          );
        })()}

        {/* Push notification banner — shown to every creator now, not just
            paying ones: free users get "answered" + reminder-nudge pushes
            (with an occasional bundled upgrade line), Premium users
            additionally get the real-time "opened" alert. Only hidden once
            they've already enabled push on this device. */}
        {!pushGranted && isPushCapable() && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderRadius: 14, background: 'var(--d-stat-bg)', border: '1.5px solid var(--d-border)', marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Bell size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, color: 'var(--d-muted)', fontWeight: 600 }}>
                {isPremium
                  ? 'Get a push notification the instant someone opens your session.'
                  : "Get notified the moment someone answers, plus reminders to nudge them."}
              </span>
            </div>
            <button onClick={handleEnablePush} disabled={pushLoading}
              style={{ padding: '9px 16px', borderRadius: 10, background: '#dc2626', color: '#fff', fontSize: 12, fontWeight: 800, border: 'none', cursor: 'pointer', opacity: pushLoading ? 0.6 : 1, whiteSpace: 'nowrap' }}>
              {pushLoading ? 'Enabling...' : 'Enable Push'}
            </button>
          </div>
        )}
        {/* iOS install banner — short, direct steps only. This is now the
            ONLY place this instruction lives (the separate floating
            InstallPrompt card was removed to avoid showing both at once). */}
        {!pushGranted && !isPushCapable() && isIosNotInstalled() && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderRadius: 14, background: 'var(--d-stat-bg)', border: '1.5px solid var(--d-border)', marginBottom: 20 }}>
            <Bell size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: 'var(--d-muted)', fontWeight: 600 }}>
              Get notified on iPhone: tap Share → "Add to Home Screen"
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="yhuu-stats">
          {[
            { num: totalSent, label: 'Total Sent', red: true },
            { num: totalCompleted, label: 'Completed', red: false },
            { num: totalWaiting, label: 'Waiting', red: totalWaiting > 0 },
          ].map(({ num, label, red }) => (
            <div key={label} style={{ background: 'linear-gradient(135deg, #e5316e 0%, #bc4968 100%)', border: '1.5px solid rgba(255,255,255,0.18)', borderRadius: 16, padding: '18px 16px' }}>
              <div className="yhuu-stat-num" style={{ color: red ? '#ffd166' : '#ffffff' }}>{num}</div>
              <div className="yhuu-stat-label" style={{ color: 'rgba(255,255,255,0.75)' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
              style={{ position: 'fixed', bottom: 48, left: '50%', transform: 'translateX(-50%)', background: 'var(--d-text)', color: 'var(--d-bg)', padding: '16px 28px', borderRadius: 9999, fontWeight: 700, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--d-border)', whiteSpace: 'nowrap' }}>
              <CheckCircle2 size={20} style={{ color: '#10b981' }} />
              {toast}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cards */}
        {loading ? (
          <div className="yhuu-grid">
            {[1, 2, 3].map(i => <div key={i} style={{ height: 280, borderRadius: 20, background: 'var(--d-card)', border: '1.5px solid var(--d-border)', animation: 'pulse 2s infinite' }} />)}
          </div>
        ) : tests.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ textAlign: 'center', padding: '64px 24px', border: '1.5px solid var(--d-border)', borderRadius: 24, background: 'var(--d-card)' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--d-bg)', border: '1.5px solid var(--d-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <MessageSquare size={28} style={{ color: 'var(--d-muted)' }} />
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--d-text)', marginBottom: 8 }}>No Active Sessions</div>
            <div style={{ fontSize: 14, color: 'var(--d-muted)', marginBottom: 28, maxWidth: 280, margin: '0 auto 28px' }}>Nothing here yet. Start your first session to see the truth.</div>
            <Link to="/create" style={{ color: '#dc2626', fontWeight: 800, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', textDecoration: 'none' }}>
              Send Your First Session →
            </Link>
          </motion.div>
        ) : (
          <div className="yhuu-grid">
            {tests.map((test, i) => {
              const isCompleted = test.status === 'completed';
              const isDispatched = test.status === 'dispatched';
              return (
                <motion.div layout key={test.id} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                  style={{ background: 'var(--d-card)', border: '1.5px solid var(--d-border)', borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>

                  <div style={{ height: 4, background: isCompleted ? 'linear-gradient(90deg,#8B3FD1 0%,#E23A82 50%,#F0483F 100%)' : 'linear-gradient(90deg,#dc2626,#ef4444)' }} />

                  {isCompleted && (
                    <div style={{ position: 'absolute', top: 4, left: 0, width: 190, height: 130, background: 'radial-gradient(circle at 0% 0%, rgba(226,58,130,0.16) 0%, rgba(226,58,130,0) 70%)', pointerEvents: 'none' }} />
                  )}

                  <div style={{ padding: '18px 18px 14px', flex: 1, position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                      <span className={isCompleted ? 'yhuu-completed-badge' : 'yhuu-sent-badge'} style={{
                        padding: '5px 11px', borderRadius: 9999, fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                        background: isCompleted ? 'var(--d-stat-bg)' : '#3b0d0d',
                        color: isCompleted ? 'var(--d-muted2)' : '#f87171',
                        border: `1.5px solid ${isCompleted ? 'var(--d-border2)' : '#7f1d1d'}`
                      }}>
                        {isCompleted ? 'Completed' : 'Sent'}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--d-muted)', fontWeight: 700 }}>
                        {new Date(test.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
                      <img src="/brand/logo-icon.png" alt="" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <div className="yhuu-card-name" style={{ marginBottom: 0 }}>{test.partner_name}</div>
                          {isCompleted && (
                            <svg width="15" height="15" viewBox="0 0 24 24" aria-label="Completed" style={{ flexShrink: 0 }}>
                              <path fill="#E23A82" d="M12 1l2.4 2.1 3.1-.6 1.1 3 3 1.1-.6 3.1L23 12l-2.1 2.4.6 3.1-3 1.1-1.1 3-3.1-.6L12 23l-2.4-2.1-3.1.6-1.1-3-3-1.1.6-3.1L1 12l2.1-2.4-.6-3.1 3-1.1 1.1-3 3.1.6z" />
                              <path fill="#fff" d="M9.8 12.6l-1.9-1.9-1.2 1.2 3.1 3.1 6-6-1.2-1.2z" />
                            </svg>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--d-muted)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginTop: 2 }}>{test.partner_contact}</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                      <span style={{ padding: '5px 10px', borderRadius: 9999, fontSize: 10, fontWeight: 800, background: 'var(--d-stat-bg)', color: 'var(--d-muted2)', border: '1.5px solid var(--d-border2)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MessageSquare size={10} />{test.questions.length} {test.questions.length === 1 ? 'item' : 'items'}
                      </span>
                      <PlatformBadge platform={test.partner_platform as PlatformId} delay={0.1 + i * 0.03} />
                    </div>

                    {/* Recipient opted into a follow-up but the sender hasn't sent it
                        yet — this is the "let them know it's accepted" signal, visible
                        right on the card, not buried inside the results page. */}
                    {test.follow_up_enabled && !test.follow_up_used && (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ padding: '5px 10px', borderRadius: 9999, fontSize: 10, fontWeight: 800, background: '#fff0f4', color: '#be123c', border: '1.5px solid #fbcfe0', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          👀 Follow-up accepted — tap to send it
                        </span>
                      </div>
                    )}
                    {/* Sender already sent the follow-up; it's out (or queued) and
                        waiting on the recipient's reply. */}
                    {isFollowUpDispatch(test) && (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ padding: '5px 10px', borderRadius: 9999, fontSize: 10, fontWeight: 800, background: '#f3e8ff', color: '#7c3aed', border: '1.5px solid #e9d5ff', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          🔁 Follow-up sent — awaiting reply
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="yhuu-card-footer" style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1.5px solid var(--d-border2)', background: 'var(--d-card2)' }}>
                    {(guessCounts[test.id!] || 0) > 0 && (
                      <Link
                        to={`/results/${test.id}`} data-track="dash_guess_pill"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 12px', borderRadius: 12, textDecoration: 'none', fontSize: 12.5, fontWeight: 900, color: '#be123c', background: '#fff0f4', border: '1.5px solid #fbcfe0' }}
                      >
                        👀 {guessCounts[test.id!]} {guessCounts[test.id!] === 1 ? 'guess' : 'guesses'} about who you are <ChevronRight size={13} />
                      </Link>
                    )}
                    {isCompleted ? (
                      <>
                        <Link to={`/results/${test.id}`} style={btn('white')}>View Report <ChevronRight size={13} /></Link>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button onClick={() => deleteTest(test.id!)} style={delBtn()}><Trash2 size={15} /></button>
                        </div>
                      </>
                    ) : isDispatched ? (
                      <>
                        <OpenedStatusSection test={test} />
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}><ReminderSection test={test} /></div>
                          <button onClick={() => deleteTest(test.id!)} style={delBtn()}><Trash2 size={15} /></button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={badge('muted')}><Clock size={13} /> Sending Soon</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button onClick={() => deleteTest(test.id!)} style={delBtn()}><Trash2 size={15} /></button>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Style helpers (all use CSS vars) ─────────────────────────────────────────
function btn(variant: 'ghost'|'gold'|'danger'|'white', disabled?: boolean): React.CSSProperties {
  const base: React.CSSProperties = {
    width: '100%', padding: '12px 10px', borderRadius: 13, fontSize: 11, fontWeight: 800,
    letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    opacity: disabled ? 0.5 : 1, textDecoration: 'none',
  };
  if (variant === 'white')  return { ...base, background: 'var(--d-text)', border: '1.5px solid var(--d-text)', color: 'var(--d-bg)' };
  if (variant === 'gold')   return { ...base, background: 'rgba(245,158,11,0.12)', border: '1.5px solid rgba(245,158,11,0.4)', color: '#f59e0b' };
  if (variant === 'danger') return { ...base, background: '#3b0d0d', border: '1.5px solid #7f1d1d', color: '#f87171' };
  return { ...base, background: 'transparent', border: '1.5px solid var(--d-border3)', color: 'var(--d-muted3)' };
}

function badge(color: 'yellow'|'muted'): React.CSSProperties {
  if (color === 'yellow') return { padding: '12px 10px', borderRadius: 13, fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'rgba(234,179,8,0.1)', border: '1.5px solid rgba(234,179,8,0.2)', color: '#eab308' };
  return { padding: '12px 10px', borderRadius: 13, fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--d-stat-bg)', border: '1.5px solid var(--d-border3)', color: 'var(--d-muted2)' };
}

function iconBtn(): React.CSSProperties {
  return { width: 36, height: 36, borderRadius: 10, background: 'var(--d-icon-bg)', border: '1.5px solid var(--d-border3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--d-muted)', cursor: 'pointer' };
}

function delBtn(): React.CSSProperties {
  return { padding: 11, color: 'var(--d-muted)', cursor: 'pointer', borderRadius: 11, border: '1.5px solid var(--d-border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
}
